/**
 * <AuthImage /> — renders an image behind a bearer-authenticated endpoint
 * without leaking the JWT into DOM src, browser history, referrers, or
 * proxy/analytics logs.
 *
 * Fetches the protected URL with `Authorization: Bearer <token>` (from
 * localStorage), converts the response body to a Blob, wraps it in an
 * object URL and points `<img src>` at that transient object URL. The
 * object URL is revoked on unmount / src change.
 */
import React, { useEffect, useRef, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthImage({ url, alt, style, className, onErrorFallback }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [errored, setErrored] = useState(false);
  const revokedRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setErrored(false);
    setObjectUrl(null);
    if (!url) return undefined;
    const token = localStorage.getItem('token');
    const full = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
    fetch(full, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => {
        if (!r.ok) throw new Error('not_ok');
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const ou = URL.createObjectURL(blob);
        revokedRef.current = ou;
        setObjectUrl(ou);
      })
      .catch(() => { if (!cancelled) setErrored(true); });
    return () => {
      cancelled = true;
      if (revokedRef.current) {
        URL.revokeObjectURL(revokedRef.current);
        revokedRef.current = null;
      }
    };
  }, [url]);

  if (errored) {
    if (onErrorFallback) return onErrorFallback;
    return null;
  }
  if (!objectUrl) {
    return <div className={className} style={{ ...style, background: 'rgba(0,0,0,0.05)' }} />;
  }
  return <img src={objectUrl} alt={alt || ''} className={className} style={style} />;
}
